// Core
import { createAsyncThunk } from '@reduxjs/toolkit';

// Boosters
import { AxiosError } from 'axios';
import {
  CreateUserT,
  ProfileResponseT,
  UpdateProfileCoins,
  UpdateTGProfileData,
  UpdateProfileEnergy,
  UpdateUserData,
  UpdateProfileEnergyResponse,
  UpdateProfileRocketCoins,
  UpdateProfileRocketCoinsResponse,
  NextCharacterUpdateResponse, UpgradeCharacterResponse, CharacterUpdateRewardsResponse, LeaderboardRewardsResponse,
  TUpdateUserWalletProps, CharacterSkinsResponse, UpdateProfileRocketUSDTResponse, UpdateProfileUSDT,
} from '../../../types';
import { axiosInstance, RootState } from '../../index';
import { RewardsType } from '../../../types/constants';
// Types

export const updateProfileCoins = createAsyncThunk<
    UpdateProfileCoins,
    UpdateProfileCoins
>(
  'profile/coins/update',
  async (quest, { rejectWithValue }) => {
    try {
      return quest;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);
export const updateProfileEnergy = createAsyncThunk<
    UpdateProfileEnergyResponse,
    UpdateProfileEnergy
>(
  'profile/energy/update',
  async (energy, { rejectWithValue, getState }) => {
    try {
      const state = (getState() as RootState);
      const userEnergy = state?.profile.userData?.energy as number;
      if (!energy.replace) {
        return Number(((userEnergy || 0) + energy.energy).toFixed(10));
      }
      return energy.energy;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);
export const updateProfileRocketCoins = createAsyncThunk<
    UpdateProfileRocketCoinsResponse,
    UpdateProfileRocketCoins
>(
  'profile/rocket-coins/update',
  async (rocketCoins, { rejectWithValue, getState }) => {
    try {
      const state = (getState() as RootState);
      const userRC = state?.profile.userData?.rocket_coins as number;
      if (!rocketCoins.replace) {
        return Number(((userRC || 0) + rocketCoins.rocketCoins).toFixed(10));
      }
      return rocketCoins.rocketCoins;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);
export const updateProfileUSDT = createAsyncThunk<
    UpdateProfileRocketUSDTResponse,
    UpdateProfileUSDT
>(
  'profile/usdt/update',
  async (usdt, { rejectWithValue, getState }) => {
    try {
      const state = (getState() as RootState);
      const userUSDT = state?.profile.userData?.usdt_balance as number;
      if (!usdt.replace) {
        return Number(((userUSDT || 0) + usdt.usdt).toFixed(10));
      }
      return usdt.usdt;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);
export const updateTGProfile = createAsyncThunk<
    UpdateTGProfileData,
    UpdateTGProfileData
>(
  'profile/tg_update',
  async (profile, { rejectWithValue }) => {
    try {
      return profile;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);
export const updateUserData = createAsyncThunk<
    UpdateUserData,
    UpdateUserData
>(
  'profile/update',
  async (profile, { rejectWithValue }) => {
    try {
      return profile;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);

export const getMe = createAsyncThunk<
    ProfileResponseT
>(
  'profile/get',
  async (profile, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('users/me');
      return data;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);

export const createUser = createAsyncThunk<
    ProfileResponseT,
    CreateUserT
>(
  'profile/create',
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('users', userData);
      return data;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);

export const getCharacterUpdate = createAsyncThunk<
    NextCharacterUpdateResponse
>(
  'profile/character/next-update',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('character/upgrade');
      return data;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);

export const updateCharacter = createAsyncThunk<
    UpgradeCharacterResponse
>(
  'profile/character/update',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch('character/upgrade');
      return data;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);
export const getCharacterSkinNames = createAsyncThunk<
    CharacterSkinsResponse
>(
  'profile/character/skinNames',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('character/upgrade/skins');
      return data;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);

export const getCharacterUpdateReward = createAsyncThunk<CharacterUpdateRewardsResponse>(
  'character_update/rewards',
  async (topPlayers, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('users/rewards/active?type=character_update');
      return data;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);

export const claimCharacterUpdateRewards = createAsyncThunk<
    LeaderboardRewardsResponse,
    {ids: number[]}
>(
  'character-update/claim/rewards',
  async ({ ids }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('users/rewards/claimAll', { ids, type: RewardsType.CHARACTER_UPDATE });
      return data;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err);
    }
  },
);

export const updateUserWallet = createAsyncThunk<ProfileResponseT, TUpdateUserWalletProps>('profile/update-wallet', async ({ wallet }, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.post<ProfileResponseT>('users/wallet', { wallet });
    return data;
  } catch (error) {
    const err = error as AxiosError;
    return rejectWithValue(err);
  }
});
